import React, { useEffect, createElement } from 'react';
import { ArrowRightIcon, SearchIcon, FileTextIcon, ShareIcon, MailIcon, TrendingUpIcon, CheckCircleIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteMarketingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website marketing | Meer bezoekers & klanten | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website marketing strategieën voor meer bezoekers en klanten. Van SEO en content marketing tot social media en e-mail. Praktische tips van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website marketing | Meer bezoekers & klanten | Noord',
      'og:description': 'Website marketing strategieën voor meer bezoekers en klanten. Van SEO en content marketing tot social media en e-mail.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-marketing',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2778">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2779">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2780">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2781" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2782" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2783" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2784">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2785">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2786">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2787">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2788">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2789">/</span>
            <span className="text-accent-light font-medium" data-id="element-2790">
              Website marketing
            </span>
          </nav>
          <p className="text-accent-light font-semibold text-sm tracking-widest uppercase mb-4 animate-fade-in-up" data-id="element-2791">
            Kennisbank
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-2792">
            Website marketing
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-2" data-id="element-2793">
            Hoe je meer bezoekers en klanten haalt uit je website met slimme
            online marketing.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 bg-white" data-id="element-2794">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2795">
          <div className="prose prose-lg prose-slate max-w-none" data-id="element-2796">
            <h2 className="text-3xl font-bold text-navy-900 mb-6" data-id="element-2797">
              Wat is website marketing?
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-2798">
              Een mooie website is pas het begin. Zonder bezoekers levert zelfs
              de beste website geen klanten op. Website marketing (of online
              marketing) is het geheel van strategieën en technieken om gericht
              verkeer naar je website te trekken en deze bezoekers om te zetten
              in betalende klanten.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8" data-id="element-2799">
              Het doel is niet alleen méér bezoekers, maar vooral de júiste
              bezoekers. Mensen die daadwerkelijk op zoek zijn naar jouw
              diensten of producten.
            </p>

            <div className="bg-navy-50 p-8 rounded-2xl border border-navy-100 mb-12" data-id="element-2800">
              <h3 className="text-xl font-bold text-navy-900 mb-4 flex items-center gap-2" data-id="element-2801">
                <TrendingUpIcon className="w-6 h-6 text-accent" data-id="element-2802" />
                Waarom is het belangrijk?
              </h3>
              <ul className="space-y-3" data-id="element-2803">
                <li className="flex items-start gap-3" data-id="element-2804">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2805" />
                  <span className="text-slate-700" data-id="element-2806">
                    Verhoogt je online zichtbaarheid in zoekmachines
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2807">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2808" />
                  <span className="text-slate-700" data-id="element-2809">
                    Bouwt vertrouwen en autoriteit op in jouw vakgebied
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2810">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2811" />
                  <span className="text-slate-700" data-id="element-2812">
                    Zorgt voor een continue stroom van nieuwe leads
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2813">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2814" />
                  <span className="text-slate-700" data-id="element-2815">
                    Meetbaar rendement op je investering (ROI)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Strategies Section */}
      <section className="py-24 bg-surface-muted" data-id="element-2816">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2817">
          <div className="text-center mb-16" data-id="element-2818">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2819">
              Marketing strategieën
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2820">
              De 4 belangrijkste pijlers om meer uit je website te halen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2821">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2822">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2823">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-2824" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2825">
                SEO optimalisatie
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2826">
                Zorg dat je website organisch gevonden wordt in Google. Door de
                juiste zoekwoorden te gebruiken en je website technisch te
                optimaliseren, trek je gratis relevant verkeer aan.
              </p>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2827">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2828">
                <FileTextIcon className="w-6 h-6 text-accent" data-id="element-2829" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2830">
                Content marketing
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2831">
                Trek bezoekers aan met waardevolle content. Blogs, kennisbank
                artikelen en cases laten je expertise zien en helpen potentiële
                klanten bij hun oriëntatieproces.
              </p>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2832">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2833">
                <ShareIcon className="w-6 h-6 text-accent" data-id="element-2834" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2835">
                Social media
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2836">
                Vergroot je bereik via kanalen zoals LinkedIn, Instagram of
                Facebook. Deel je website content en stuur geïnteresseerd
                verkeer direct naar je landingspagina's.
              </p>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2837">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2838">
                <MailIcon className="w-6 h-6 text-accent" data-id="element-2839" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2840">
                E-mail marketing
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2841">
                Bouw een relatie op met je doelgroep. Verzamel e-mailadressen
                via je website en stuur gerichte nieuwsbrieven om top-of-mind te
                blijven bij je potentiële klanten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2842">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2843">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2844">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2845">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2846">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2847">•</span>
            <a href="#/website-tips" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2848">
              Website tips
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2849">•</span>
            <a href="#/kennisbank" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2850">
              Kennisbank
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2851">•</span>
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2852">
              Homepage
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2853" />
    </main>;
}